import type { UserRole } from '../types/user';

// Every main-app permission column on user_roles. The RLS policies read the
// same columns, so what the admin panel grants is what the database allows.
export const PERMISSION_KEYS = [
  'can_view_campaigns', 'can_edit_campaigns', 'can_delete_campaigns',
  'can_view_clients', 'can_edit_clients',
  'can_view_influencers', 'can_edit_influencers',
  'can_view_payments', 'can_view_commissions', 'can_edit_commissions',
  'can_manage_users', 'can_view_statistics',
  'can_edit_others_campaigns', 'can_view_others_campaigns',
  'can_view_admin_campaigns', 'can_delete_others_campaigns',
  'can_issue_invoices',
] as const;

export type PermissionKey = typeof PERMISSION_KEYS[number];
export type PermissionSet = Record<PermissionKey, boolean>;

export interface PermissionItem {
  key: PermissionKey;
  labelKey: string;
  hintKey?: string;
}

/**
 * What the admin panel lets you toggle, one switch per column. Left out:
 * can_view_campaigns (every staff member sees their own campaigns),
 * can_edit_commissions (nothing in the app edits commissions) and
 * can_manage_users (that is the Administrator profile).
 */
export const PERMISSION_GROUPS: { labelKey: string; items: PermissionItem[] }[] = [
  {
    labelKey: 'admin.permGroupCampaigns',
    items: [
      { key: 'can_edit_campaigns', labelKey: 'admin.permEditOwn' },
      { key: 'can_view_others_campaigns', labelKey: 'admin.permViewOthers' },
      { key: 'can_edit_others_campaigns', labelKey: 'admin.permEditOthers' },
      { key: 'can_delete_campaigns', labelKey: 'admin.permDeleteOwn' },
      { key: 'can_delete_others_campaigns', labelKey: 'admin.permDeleteOthers' },
      { key: 'can_view_admin_campaigns', labelKey: 'admin.permAdminCampaigns', hintKey: 'admin.permAdminCampaignsHint' },
    ],
  },
  {
    labelKey: 'admin.permGroupClients',
    items: [
      { key: 'can_view_clients', labelKey: 'admin.permViewClients' },
      { key: 'can_edit_clients', labelKey: 'admin.permEditClients' },
    ],
  },
  {
    labelKey: 'admin.permGroupCreators',
    items: [
      { key: 'can_view_influencers', labelKey: 'admin.permViewCreators' },
      { key: 'can_edit_influencers', labelKey: 'admin.permEditCreators' },
    ],
  },
  {
    labelKey: 'admin.permGroupFinance',
    items: [
      { key: 'can_view_payments', labelKey: 'admin.permPayments' },
      { key: 'can_view_commissions', labelKey: 'admin.permCommissions' },
      { key: 'can_view_statistics', labelKey: 'admin.permStatistics' },
      { key: 'can_issue_invoices', labelKey: 'admin.permIssueInvoices', hintKey: 'admin.permIssueInvoicesHint' },
    ],
  },
];

/** Turning one on turns these on; turning one of these off turns it off. */
const REQUIRES: Partial<Record<PermissionKey, PermissionKey[]>> = {
  can_edit_others_campaigns: ['can_view_others_campaigns', 'can_edit_campaigns'],
  can_delete_campaigns: ['can_edit_campaigns'],
  can_delete_others_campaigns: ['can_delete_campaigns', 'can_edit_others_campaigns'],
  can_edit_clients: ['can_view_clients'],
  can_edit_influencers: ['can_view_influencers'],
};

export function setPermission(perms: PermissionSet, key: PermissionKey, on: boolean): PermissionSet {
  const next = { ...perms, [key]: on };
  if (on) {
    for (const required of REQUIRES[key] || []) {
      if (!next[required]) Object.assign(next, setPermission(next, required, true));
    }
  } else {
    for (const [dependent, required] of Object.entries(REQUIRES) as [PermissionKey, PermissionKey[]][]) {
      if (required.includes(key) && next[dependent]) Object.assign(next, setPermission(next, dependent, false));
    }
  }
  return next;
}

/** Whether `key` is on only because something that needs it is on. */
export function requiredBy(perms: PermissionSet, key: PermissionKey): PermissionKey | null {
  for (const [dependent, required] of Object.entries(REQUIRES) as [PermissionKey, PermissionKey[]][]) {
    if (required.includes(key) && perms[dependent]) return dependent;
  }
  return null;
}

export type StaffProfile = 'admin' | 'supervisor' | 'agent';

const ALL_ON = Object.fromEntries(PERMISSION_KEYS.map(k => [k, true])) as PermissionSet;

export const PROFILE_PRESETS: Record<StaffProfile, { role: 'admin' | 'agent' } & PermissionSet> = {
  // Issuing invoices is a person's job, not part of any profile
  admin: { role: 'admin', ...ALL_ON, can_issue_invoices: false },
  supervisor: {
    role: 'agent',
    ...ALL_ON,
    can_edit_commissions: false,
    can_manage_users: false,
    can_issue_invoices: false,
  },
  agent: {
    role: 'agent',
    can_view_campaigns: true, can_edit_campaigns: true, can_delete_campaigns: false,
    can_view_clients: true, can_edit_clients: true,
    can_view_influencers: true, can_edit_influencers: true,
    can_view_payments: false, can_view_commissions: false, can_edit_commissions: false,
    can_manage_users: false, can_view_statistics: false,
    can_edit_others_campaigns: false, can_view_others_campaigns: false,
    can_view_admin_campaigns: false, can_delete_others_campaigns: false,
    can_issue_invoices: false,
  },
};

/** Content Hub only: no main-app permissions at all. */
export const NO_MAIN_APP_ACCESS = Object.fromEntries(PERMISSION_KEYS.map(k => [k, false])) as PermissionSet;

const TOGGLED_KEYS = PERMISSION_GROUPS.flatMap(g => g.items.map(i => i.key));

/** The preset a person matches, or 'custom' once any switch differs from it. */
export function deriveProfile(u: Pick<UserRole, 'role'> & Partial<PermissionSet>): StaffProfile | 'custom' | 'influencer' {
  if (u.role === 'admin') return 'admin';
  if (u.role === 'influencer') return 'influencer';
  for (const profile of ['supervisor', 'agent'] as const) {
    const preset = PROFILE_PRESETS[profile];
    if (TOGGLED_KEYS.every(k => !!u[k] === preset[k])) return profile;
  }
  return 'custom';
}

export function pickPermissions(source: Partial<PermissionSet>): PermissionSet {
  return Object.fromEntries(PERMISSION_KEYS.map(k => [k, !!source[k]])) as PermissionSet;
}

// ── Campaign actions (mirror the campaigns RLS policies) ─────────────────────

type RoleLike = Partial<PermissionSet> | null | undefined;

export function canEditCampaign(role: RoleLike, campaign: { agent_id?: string | null }, userId?: string): boolean {
  if (!role) return false;
  if (role.can_manage_users) return true;
  if (!role.can_edit_campaigns) return false;
  return (!!userId && campaign.agent_id === userId) || !!role.can_edit_others_campaigns;
}

export function canDeleteCampaign(role: RoleLike, campaign: { agent_id?: string | null }, userId?: string): boolean {
  if (!role) return false;
  if (role.can_manage_users) return true;
  if (!role.can_delete_campaigns) return false;
  return (!!userId && campaign.agent_id === userId) || !!role.can_delete_others_campaigns;
}
